import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { Alert, Button } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import foundryMarkdown from "../../../lib/FoundryMarkdown";
import { markdownComponents } from "../../components/MarkdownComponents/MarkdownComponents";
import ContentLoader from "../../components/ContentLoader/ContentLoader";
import ErrorState from "../../components/ErrorState/ErrorState";
import InternalLinkSourcePill from "../../components/InternalLinkSourcePill/InternalLinkSourcePill";
import {
  SharedToLinkedInToast,
  SharedToLinkedInToastHandle,
} from "../../components/SharedToLinkedInToast/SharedToLinkedInToast";
import {
  ShareDialog,
  ShareDialogHandle,
} from "../../components/ShareDialog/ShareDialog";
import { useServices } from "../../services/Services";
import { getGlobalReportTypeBySlug } from "../../type/globalReportTypes";
import {
  cleanHtmlForCopy,
  extractFoundryObjectReferences,
  buildSourceNumberMap,
} from "../../helper/helper";
import { useEventSources } from "../../hooks/useEventSources";
import { usePageTitle } from "../../context/usePageTitle";
import { format } from "date-fns";
import type { Osdk } from "@osdk/client";
import type { Analysis } from "@unruly-osdk/sdk";
import css from "../../components/ReportContainer/ReportContainer.module.css";

export function GlobalReportPage() {
  const { reportSlug } = useParams<{ reportSlug: string }>();
  const navigate = useNavigate();
  const services = useServices();
  const { setPageTitle } = usePageTitle();
  const reportContentRef = useRef<HTMLDivElement>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const toastRef = useRef<SharedToLinkedInToastHandle>(null);
  const shareDialogRef = useRef<ShareDialogHandle>(null);

  const type = reportSlug ? getGlobalReportTypeBySlug(reportSlug) : undefined;
  const {
    data: report,
    isFetched,
    isLoading,
    isError,
    error,
    refetch: refetchReport,
  } = services.GlobalReport.useGetGlobalReportBySlug(reportSlug);
  const { data: user } = services.User.useGetCurrent();
  const referencedEvents = extractFoundryObjectReferences(
    report?.reportText ?? "",
  );
  const sourceNumberMap = buildSourceNumberMap(referencedEvents);
  const {
    eventSources,
    isLoading: isEventSourcesLoading,
    missingEventIds,
  } = useEventSources(referencedEvents);

  const handleOpenAnalysis = (
    analysis: Osdk.Instance<Analysis>,
    _eventId: string,
  ) => {
    const route =
      analysis.type === "event deep dive"
        ? `/event-analysis/${analysis.id}`
        : analysis.type === "topic deep dive"
          ? `/topic-analysis/${analysis.id}`
          : "/";
    navigate(route);
  };

  const title = type?.title ?? "";
  const subTitle =
    report?.generatedAt != null
      ? `Global Research · ${format(new Date(report.generatedAt), "EEE, MMM dd, yyyy")}`
      : "Global Research";

  useEffect(() => {
    if (type) {
      setPageTitle(title, subTitle);
      return () => setPageTitle("", "");
    }
  }, [type, title, subTitle, setPageTitle]);

  if (reportSlug && !type) {
    navigate("/global-research", { replace: true });
    return null;
  }

  if (isFetched && report === null) {
    return (
      <div className={css.centered}>
        <Alert severity="warning">Research not found</Alert>
        <Button
          variant="contained"
          component={Link}
          to="/global-research"
          startIcon={<ArrowBack />}
          className={css.backLink}
        >
          Back to Global Research
        </Button>
      </div>
    );
  }

  const copyReportToClipboard = async () => {
    try {
      if (!reportContentRef.current) return;
      const cleanedHtml = cleanHtmlForCopy(reportContentRef.current);
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([cleanedHtml], { type: "text/html" }),
        "text/plain": new Blob([reportContentRef.current.textContent ?? ""], {
          type: "text/plain",
        }),
      });
      await navigator.clipboard.write([clipboardItem]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy report:", err);
    }
  };

  const markdown = report?.reportText ?? null;

  return (
    <div className={css["report-container"]}>
      <div className={css["report-header"]}>
        <div className={css["report-title-section"]}>
          <Link
            to="/global-research"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              marginBottom: "8px",
              fontSize: "14px",
              color: "var(--main-text)",
              textDecoration: "none",
            }}
          >
            <ArrowBack sx={{ fontSize: 18 }} />
            Back to Global Research
          </Link>
          <h1 className={css["report-title"]}>{title}</h1>
          <p className={css["report-subtitle"]}>{subTitle}</p>
        </div>
        <div className={css["report-actions"]}>
          {isError ? null : isLoading ? (
            <ContentLoader type="short-text" />
          ) : (
            <Button
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={copyReportToClipboard}
              sx={{ textTransform: "none" }}
            >
              {copySuccess ? "Copied!" : "Copy Content"}
            </Button>
          )}
        </div>
      </div>

      {isError && (
        <ErrorState
          error={error}
          onRetry={() => refetchReport()}
          variant="section"
        />
      )}

      <div className={css["report-content"]} ref={reportContentRef}>
        {isError ? null : isLoading ? (
          <ContentLoader type="report-content" />
        ) : markdown ? (
          <div className="global-report-body">
            <Markdown
              remarkPlugins={[remarkGfm, foundryMarkdown]}
              components={{
                ...markdownComponents,
                a({ children, href }) {
                  const isInternalLink = href && href.startsWith("/");
                  if (isInternalLink) {
                    return (
                      <InternalLinkSourcePill
                        href={href}
                        eventSources={eventSources}
                        isLoading={isEventSourcesLoading}
                        missingEventIds={missingEventIds}
                        userId={user?.id}
                        sourceNumberMap={sourceNumberMap}
                        onOpenAnalysis={handleOpenAnalysis}
                        showShareDialog={(eventId) =>
                          shareDialogRef.current?.show(eventId)
                        }
                      />
                    );
                  }
                  return (
                    <a
                      href={href || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {markdown}
            </Markdown>
            <ShareDialog
              onShareSuccess={(postId) =>
                toastRef.current?.shareSuccess(postId)
              }
              ref={shareDialogRef}
            />
            <SharedToLinkedInToast ref={toastRef} />
          </div>
        ) : isFetched ? (
          <p style={{ color: "var(--muted-text)" }}>
            No research content available yet.
          </p>
        ) : null}
      </div>
    </div>
  );
}
